import { useCallback, useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import apiClient from '../api/api';
import { API_ENDPOINTS } from '../endpoints';
import { createSessionPersistor } from '../utils/sessionStorage';

const formatDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const newUsersPersistor = createSessionPersistor('new-users-page', {
  fromDate: '',
  toDate: '',
  users: [],
});

const useNewUsersPageState = () => {
  const today = formatDate(new Date());
  const yDate = new Date();
  yDate.setDate(yDate.getDate() - 1);
  const yesterday = formatDate(yDate);

  const weekAgo = formatDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
  const persisted = newUsersPersistor.load();
  const [fromDate, setFromDate] = useState(persisted.fromDate || today);
  const [toDate, setToDate] = useState(persisted.toDate || today);
  const [users, setUsers] = useState(persisted.users || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [institutionFilter, setInstitutionFilter] = useState('');

  useEffect(() => {
    newUsersPersistor.save({
      fromDate,
      toDate,
      users,
    });
  }, [fromDate, toDate, users]);

  const [summaryOpen, setSummaryOpen] = useState(false);
  const institutions = Array.from(new Set(users.map(u => u.institutionCode).filter(Boolean))).sort();

  const institutionBreakdown = institutions.reduce((acc, inst) => {
    const instUsers = users.filter(u => u.institutionCode === inst);
    acc[inst] = {
      total: instUsers.length,
      attempts: instUsers.filter(u => u.lstSubmittedDate).length,
    };
    return acc;
  }, {});

  const filteredUsers = useCallback(() => {
    let result = users;

    if (institutionFilter) {
      result = result.filter(u => u.institutionCode === institutionFilter);
    }

    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter(u =>
        (u.fname && u.fname.toLowerCase().includes(lowerQuery)) ||
        (u.lname && u.lname.toLowerCase().includes(lowerQuery)) ||
        (u.email && u.email.toLowerCase().includes(lowerQuery)) ||
        (u.phoneNo && String(u.phoneNo).includes(lowerQuery)) ||
        (String(u.id).includes(lowerQuery))
      );
    }

    return result;
  }, [users, searchQuery, institutionFilter])();

  const [overallStatsCount, setOverallStatsCount] = useState(0);
  const [overallStats, setOverallStats] = useState([]);

  const overallInstitutionBreakdown = useMemo(() => {
    const breakdown = {};
    overallStats.forEach(item => {
      const code = item.institution?.code;
      if (code) {
        breakdown[code] = (breakdown[code] || 0) + 1;
      }
    });
    return breakdown;
  }, [overallStats]);

  const fetchUsers = useCallback(async (e) => {
    const isManual = !!e;

    if (!fromDate || !toDate) {
      setError('Please select a date range.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const getNextDay = (dateStr) => {
        const date = new Date(dateStr);
        date.setDate(date.getDate() + 1);
        return date.toISOString().slice(0, 10);
      };

      const apiToDate = getNextDay(toDate);
      const [usersResponse, statsResponse] = await Promise.all([
        apiClient.get(`${API_ENDPOINTS.usersByDateRange}?fromDate=${fromDate}&toDate=${apiToDate}`),
        apiClient.get(`${API_ENDPOINTS.getTodayUserStats}?startDate=${fromDate}&endDate=${apiToDate}`)
      ]);
      const data = Array.isArray(usersResponse.data) ? usersResponse.data : [];
      const statsData = Array.isArray(statsResponse.data) ? statsResponse.data : [];

      setUsers(data);
      setOverallStats(statsData);
      setOverallStatsCount(statsData.length);

      if (isManual) {
        setSummaryOpen(true);
      }

    } catch (err) {
      console.error('Failed to fetch users or stats', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    if (!fromDate || fromDate > toDate) {
      setFromDate(today);
    }
  }, []);

  useEffect(() => {
    if (users.length === 0 && !loading && fromDate && toDate) {
      fetchUsers();
    }
  }, [fromDate, toDate]);

  const clearAll = useCallback(() => {
    setFromDate(today);
    setToDate(today);
    setUsers([]);
    setOverallStats([]);
    setSearchQuery('');
    setInstitutionFilter('');
    setError(null);
    setSummaryOpen(false);
    newUsersPersistor.clear();
  }, [yesterday, today]);

  return {
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    users,
    filteredUsers,
    institutions,
    institutionBreakdown,
    overallInstitutionBreakdown,
    institutionFilter,
    setInstitutionFilter,
    searchQuery,
    setSearchQuery,
    loading,
    error,
    fetchUsers,
    clearAll,
    summaryOpen,
    setSummaryOpen,
    overallStatsCount,
    today,
  };
};

export default useNewUsersPageState;